import { Icon } from "@opendash/icons";
import { Button, message } from "antd";
import { observer } from "mobx-react-lite";

type Props = {
  data: Record<string, any>[];
  filename: string;
  columns?: string[];
};

/**
 * Utility function to export data to CSV format
 */
const exportToCSV = (data: Record<string, any>[], filename: string) => {
  if (data.length === 0) {
    message.warning("Keine Daten zum Exportieren vorhanden");
    return;
  }

  // Get all unique keys from all objects
  const keys = Array.from(
    new Set(data.flatMap((obj) => Object.keys(obj)))
  );

  // Build CSV content
  const headers = keys.join(";");
  const rows = data
    .map((obj) =>
      keys
        .map((key) => {
          const value = obj[key];
          if (value === null || value === undefined) return "";
          // Escape quotes and wrap in quotes if contains special characters
          const stringValue = String(value);
          const needsQuotes =
            stringValue.includes(";") ||
            stringValue.includes('"') ||
            stringValue.includes("\n");
          return needsQuotes ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
        })
        .join(";")
    )
    .join("\n");

  const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(
    headers + "\n" + rows
  )}`;

  // Trigger download
  const link = document.createElement("a");
  link.href = csvContent;
  link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  message.success(`${data.length} Einträge exportiert`);
};

/**
 * Export button component that exports table data to CSV
 */
export const ExportButton = observer((props: Props) => {
  const { data, filename, columns } = props;

  const handleExport = () => {
    // Filter data to only include specified columns if provided
    let exportData = data;
    if (columns && columns.length > 0) {
      exportData = data.map((obj) => {
        const filtered: Record<string, any> = {};
        columns.forEach((col) => {
          filtered[col] = obj[col];
        });
        return filtered;
      });
    }

    exportToCSV(exportData, filename);
  };

  return (
    <Button
      onClick={handleExport}
      icon={<Icon icon="fa:download" />}
      size="small"
    >
      CSV Export
    </Button>
  );
});

/**
 * Export utilities
 */
export const ExportUtils = {
  toCSV: exportToCSV,
};
